import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { Paged } from 'api/common/types/Page';
import styles from './MaintenanceContractTypesList.module.scss';
import InputSearch from 'common/components/inputSearch/InputSearch';
import { useDebouncedCallback } from 'use-debounce';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/dropdown/DropdownItem';
import { FaEllipsisH } from 'react-icons/fa';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import { MaintenanceContractTypeDto } from 'api/maintenanceContractTypes/models/MaintenanceContractTypeDto';
import { MaintenanceContractTypesSearchCriteria } from 'api/maintenanceContractTypes/models/MaintenanceContractTypesSearchCriteria';
import MaintenanceContractTypesService from 'api/maintenanceContractTypes/MaintenanceContractTypesService';
import { useHistory } from 'react-router-dom';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import AddIcon from 'assets/svg/desktop_add_new.svg';

type Props = {
};

const MaintenanceContractTypesList: React.FC<Props> = ({ }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const isFirstLoad = useRef(true);
    const filterJsonKey = 'PARAMETERS_MAINTENANCE_CONTRACT_TYPE_FILTER';

    const [maintenanceContractTypesPage, setMaintenanceContractTypesPage] = useState<Paged<MaintenanceContractTypeDto>>();
    const [criteria, setCriteria] = useState<MaintenanceContractTypesSearchCriteria>({
        translations: t('common.maintenance_contract_type', { returnObjects: true }),
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'translated_name',
        orderBy: 'asc'
    });

    const history = useHistory();

    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<MaintenanceContractTypeDto | null>(null);

    const showRemoveItemDialog = (item: MaintenanceContractTypeDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    };

    const renderTableActionCell = (row: MaintenanceContractTypeDto) => {
        return (
            <Dropdown
                options={
                    <>
                        <DropdownItem url={`/settings/maintenanceContractTypes/details/${row.id ?? ''}`}>
                            {t('common.details')}
                        </DropdownItem>
                        <DropdownItem url={`/settings/maintenanceContractTypes/edit/${row.id ?? ''}`}>
                            {t('common.edit')}
                        </DropdownItem>
                        <DropdownItem onClick={() => showRemoveItemDialog(row)} disabled={row.isSystem} disabled_message={t('maintenance_types.forbidden_delete_tooltip')}>
                            {t('common.remove')}
                        </DropdownItem>
                    </>
                }
            >
                <div>
                    <FaEllipsisH />
                </div>
            </Dropdown>
        );
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const renderNameCell = (row: MaintenanceContractTypeDto) => {
        return <div>{row.translatedName}</div>
    }

    const tableColumns: ListingTableColumn<MaintenanceContractTypeDto>[] = [
        {
            field: 'name',
            name: t('maintenance_types.list.name'),
            onSearch: onTableFilter,
            searchField: 'translated_name',
            renderCell: renderNameCell
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderTableActionCell,
            cellStyle: { overflow: 'unset' },
        },
    ];

    const getData = async () => {
        try {
            Loading.show();

            saveOrdernationFilters();

            const page = await MaintenanceContractTypesService.getList(criteria);
            setMaintenanceContractTypesPage(page);

            Loading.hide();
        } catch (error) {
            Logger.error(
                LOGGER_LOG_TYPE.REQUEST,
                `Couldn't get maintenance contract types list`,
                error
            );
            addToast(t('common.messages.error_load_info'), {
                appearance: 'error',
            });
            Loading.hide();
        }
    };

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            await MaintenanceContractTypesService.remove(itemToRemove);
            setCriteria({ ...criteria, page: 1 });
            onCancelRemove();
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete maintenance contract type`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    const debounced = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        setCriteria({ ...criteria, page: 1, allIn: value });
    }, 500);

    useEffect(() => {
        if (isFirstLoad.current == true) {
            loadFilters()
        } else {
            getData()
        }
    }, [criteria]);

    const saveOrdernationFilters = () => {
        saveFiltersOnCache(criteria);
    }

    const saveFiltersOnCache = (f: MaintenanceContractTypesSearchCriteria) => {
        localStorage.setItem(filterJsonKey, JSON.stringify(f));
    }

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(filterJsonKey);
        if (cachedFilters != null) { onChangeFilters(JSON.parse(cachedFilters), true) }
        else { getData() }

        isFirstLoad.current = false;
    }

    const onChangeFilters = (f: MaintenanceContractTypesSearchCriteria, isOnLoad?: boolean) => {
        saveFiltersOnCache(f);
        setCriteria(prev => ({
            ...prev,
            ...f,
            translations: t('common.maintenance_contract_type', { returnObjects: true }),
            page: (isOnLoad && f.page) ? f.page : 1,
        }))
    }

    return (
        <div className='list'>
            <div className={styles.tabHeader}>
                <div>
                    <InputSearch
                        onChangeValue={debounced}
                        placeholder={t('maintenance_types.search_placeholder')}
                        defaultValue={criteria.allIn}
                    ></InputSearch>
                </div>
                <div>
                    <ScreenHeaderButton icon={AddIcon} onClick={() => history.push(`/settings/maintenanceContractTypes/new`)} />
                </div>
            </div>
            {!isFirstLoad.current && <ListingTable
                columns={tableColumns}
                rows={maintenanceContractTypesPage?.items || []}
                onRowClick={row => history.push(`/settings/maintenanceContractTypes/details/${row.id ?? ''}`)}
                onHref={row => `/settings/maintenanceContractTypes/details/${row.id ?? ''}`}
                allowHover={true}
                initialSearch={{ colField: criteria.orderColumn ?? 'translated_name', isOrderAsc: (criteria.orderBy && criteria.orderBy == 'asc') ? true : false }}
            />}
            <PaginationWithInfo
                itemName={t('maintenance_types.title')}
                currentPage={maintenanceContractTypesPage?.currentPage || 1}
                pageItems={maintenanceContractTypesPage?.items.length || 0}
                totalItems={maintenanceContractTypesPage?.totalItems || 0}
                onChange={page => setCriteria({ ...criteria, page })}
            />
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('common.messages.remove_maintenance_type', { name: itemToRemove?.translatedName ?? '' })} />

        </div>
    );
};

export default MaintenanceContractTypesList;
